/**
 * Password input with a show/hide toggle.
 *
 * A drop-in replacement for a `<input type="password">` — pass the same props
 * (id, value, onChange, className, autoComplete, disabled, placeholder). An eye
 * button toggles the field between masked and plain text so the user can verify
 * what they typed. The toggle is `type="button"` (never submits) and excluded
 * from the tab order so it doesn't interrupt keyboard sign-in.
 *
 * @module components/PasswordInput
 */

import { useState, type ReactElement, type InputHTMLAttributes } from 'react';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

const EyeIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3.5 7 10 7a9.12 9.12 0 0 0 5.39-1.61" />
    <line x1="2" x2="22" y1="2" y2="22" />
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
  </svg>
);

export function PasswordInput({ className = '', disabled, ...props }: Props): ReactElement {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        {...props}
        disabled={disabled}
        type={visible ? 'text' : 'password'}
        className={`${className} pr-10`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        tabIndex={-1}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-600 disabled:opacity-50"
      >
        {visible ? EyeOffIcon : EyeIcon}
      </button>
    </div>
  );
}
